import React, { useState } from "react";
import { branchList } from "../data/branches";

interface Branch {
  id: number;
  name: string;
  address: string;
  postalCode: string;
  lat: number;
  lng: number;
}

interface BranchWithDistance extends Branch {
  distance: number;
  distanceText: string;
}

interface IBranch {
  onSelectBranch: (branch: BranchWithDistance) => void;
  selectedBranch: BranchWithDistance
  setPostalCode: any;
  postalCode: any;
}

//"65428"

const BranchSearch: React.FC<IBranch> = ({ onSelectBranch, selectedBranch, setPostalCode, postalCode }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;


  const [branches] = useState<Branch[]>(branchList);
  const [nearestBranch, setNearestBranch] = useState<BranchWithDistance | null>(null);
  const [sortedBranches, setSortedBranches] = useState<BranchWithDistance[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const chunkArray = (array: string[], chunkSize: number): string[][] => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const fetchDistanceMatrix = async (origins: string, destinations: string[]): Promise<any[]> => {
    const destinationChunks = chunkArray(destinations, 25);
    const allResults: any[] = [];

    //https://konfiguration-backend-hwk1stq70-sadafs-projects-1d8b7d4f.vercel.app/api

    const API_PATH = "https://konfig-kd-ueberdachung.de/api"


    for (const chunk of destinationChunks) {
      const url = `${API_PATH}/distance-matrix?origins=${origins}&destinations=${chunk.join(
        "|"
      )}&key=${apiKey}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.status !== "OK") {
          throw new Error(data.error_message || "Failed to fetch distance data");
        }

        allResults.push(...data.rows[0].elements);
      } catch (error) {
        console.error("Error fetching Distance Matrix API:", error);
        throw error;
      }
    }

    return allResults;
  };

  const handleSearch = async () => {
    if (!postalCode) {
      setError("Please enter a valid postal code.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const geocodeResponse = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(postalCode)}&key=${apiKey}`
      );
      const geocodeData = await geocodeResponse.json();

      if (geocodeData.status !== "OK" || geocodeData.results.length === 0) {
        setError("Ungültige Postleitzahl. Bitte versuchen Sie es erneut.");
        setLoading(false);
        return;
      }

      const userLocation = geocodeData.results[0].geometry.location;
      const origins = `${userLocation.lat},${userLocation.lng}`;
      const destinations = branches.map((branch) => `${branch.lat},${branch.lng}`);

      const distanceElements = await fetchDistanceMatrix(origins, destinations);

      const distances: BranchWithDistance[] = distanceElements.map(
        (element: any, index: number) => ({
          ...branches[index],
          distance: element.distance.value,
          distanceText: element.distance.text,
        })
      );

      const sorted = distances.sort((a, b) => a.distance - b.distance);

      setNearestBranch(sorted[0]);
      setSortedBranches(sorted);
      setLoading(false);
    } catch (error) {
      setError("An error occurred. Please try again later.");
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="p-2 rounded-3xl shadow-md mb-6 bg-gray-100">
        <h2 className="text-lg md:text-xl font-bold text-center mb-4">Postleitzahl eingeben</h2>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Postleitzahl eingeben"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            className="w-1/2 px-3 py-3 shadow bg-white text-base md:text-lg border-none rounded-full"
          />
          <button
            onClick={handleSearch}
            disabled={!Boolean(postalCode)}
            className={`w-1/2 bg-white shadow text-black px-3 py-3 rounded-full font-bold text-base md:text-lg hover:text-gray-600 transition-colors flex items-center justify-center ${Boolean(postalCode) ? "" : "opacity-50"
              }`}
          >
            Suchen <span className="text-red-600 ml-2">&gt;</span>
          </button>
        </div>
      </div>


      {sortedBranches?.length > 0 && <div className="bg-gray-100 p-4 rounded-3xl shadow-md max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 scrollbar-thumb-rounded-full">
        {sortedBranches.map((branch) => (
          <div
            key={branch.id}
            onClick={() => onSelectBranch(branch)}
            className={`flex items-center justify-between bg-white p-4 rounded-2xl shadow mb-4 ${branch.id === selectedBranch?.id ? "border-2 border-red-600" : ""
              }`}
          >
            <div className="flex flex-col">
              <h3 className="text-sm md:text-lg font-bold">{branch.name}</h3>
              <p className="text-xs md:text-sm text-gray-600">{branch.address}</p>
              <p className="text-xs md:text-sm text-gray-600">{branch.distanceText}</p>
            </div>
            <button
              onClick={() => onSelectBranch(branch)}
              className="bg-transparent flex items-center text-black px-3 md:px-4 py-2 rounded-full font-bold text-xs md:text-sm hover:text-gray-600 transition-colors"
            >
              Auswählen <span className="text-red-600 ml-2">&gt;</span>
            </button>
          </div>
        ))}
      </div>}

      {error && (
        <div className="text-red-600 font-bold text-center mt-4 text-sm md:text-base">{error}</div>
      )}

      {loading && (
        <div className="text-gray-500 font-bold text-center mt-4 text-sm md:text-base">Loading...</div>
      )}
    </div>
  );
};

export default BranchSearch;
